/**
 * Identity audit Graph calls (C8) — ported from x365 graph.js, behind the graph/
 * layer (§3.4). Typed, least-privilege, zero-egress. Endpoints are recorded here so
 * evidence records can cite their exact provenance.
 */
import { graphBatch, graphGet, graphGetAll } from '@/graph/client';
import { CAPABILITY_SCOPES } from '@/auth/scopes';

const SCOPES = CAPABILITY_SCOPES.C8.scopes;

/** Endpoint constants — reused as evidence provenance (MERGE-PLAN §4.2). */
export const IDENTITY_ENDPOINTS = {
  users: '/users',
  mfaReport: '/reports/authenticationMethods/userRegistrationDetails',
  caPolicies: '/identity/conditionalAccess/policies',
  directoryRoles: '/directoryRoles',
  pim: '/roleManagement/directory/roleEligibilityScheduleInstances',
  securityDefaults: '/policies/identitySecurityDefaultsEnforcementPolicy',
} as const;

export interface AuditUser {
  id: string;
  displayName: string | null;
  userPrincipalName: string | null;
  accountEnabled?: boolean;
  userType?: string | null;
  assignedLicenses?: { skuId?: string }[];
  createdDateTime?: string;
  signInActivity?: { lastSignInDateTime?: string | null } | null;
}

export interface MfaRegistration {
  userPrincipalName?: string | null;
  isMfaRegistered?: boolean;
  isMfaCapable?: boolean;
}

export interface CaPolicy {
  id: string;
  displayName?: string | null;
  state?: string;
  conditions?: {
    users?: { includeUsers?: string[]; includeGroups?: string[] };
    clientAppTypes?: string[];
  };
  grantControls?: { operator?: string; builtInControls?: string[] };
}

export interface RoleMemberRef {
  id: string;
  displayName?: string | null;
  userPrincipalName?: string | null;
}

export interface DirectoryRoleWithMembers {
  id: string;
  displayName?: string | null;
  roleTemplateId?: string;
  members: RoleMemberRef[];
}

export interface PimAssignment {
  id: string;
  principalId?: string;
  roleDefinitionId?: string;
  status?: string;
}

export function getUsers(): Promise<AuditUser[]> {
  return graphGetAll<AuditUser>(IDENTITY_ENDPOINTS.users, {
    scopes: SCOPES,
    query: {
      $select:
        'id,displayName,userPrincipalName,accountEnabled,assignedLicenses,userType,signInActivity,createdDateTime',
      $top: '999',
    },
  });
}

export function getMfaRegistrationReport(): Promise<MfaRegistration[]> {
  return graphGetAll<MfaRegistration>(IDENTITY_ENDPOINTS.mfaReport, { scopes: SCOPES });
}

export function getConditionalAccessPolicies(): Promise<CaPolicy[]> {
  return graphGetAll<CaPolicy>(IDENTITY_ENDPOINTS.caPolicies, { scopes: SCOPES });
}

export async function getDirectoryRoleMembers(): Promise<DirectoryRoleWithMembers[]> {
  const roles = await graphGetAll<{ id: string; displayName?: string; roleTemplateId?: string }>(
    IDENTITY_ENDPOINTS.directoryRoles,
    { scopes: SCOPES, query: { $select: 'id,displayName,roleTemplateId' } },
  );
  if (roles.length === 0) return [];
  const responses = await graphBatch<{ value?: RoleMemberRef[] }>(
    roles.map((r) => ({
      id: r.id,
      method: 'GET',
      url: `/directoryRoles/${r.id}/members?$select=id,displayName,userPrincipalName`,
    })),
    { scopes: SCOPES },
  );
  return roles.map((r) => ({
    ...r,
    members: responses.find((res) => res.id === r.id)?.body?.value ?? [],
  }));
}

export function getPimAssignments(): Promise<PimAssignment[]> {
  return graphGetAll<PimAssignment>(IDENTITY_ENDPOINTS.pim, {
    scopes: SCOPES,
    query: { $select: 'id,principalId,roleDefinitionId,status' },
  });
}

export async function getSecurityDefaults(): Promise<{ isEnabled: boolean } | null> {
  try {
    const data = await graphGet<{ isEnabled?: boolean }>(
      IDENTITY_ENDPOINTS.securityDefaults,
      { scopes: SCOPES },
    );
    return { isEnabled: !!data.isEnabled };
  } catch {
    return null;
  }
}
